import React from "react";
import cn from "classnames";
import { Input, Radio, Label } from "@trussle/external-components";
import { CalculatorTooltip } from "../../calculatorTooltip";
import { IStampDutyCountry, IStampDutyYesNoFirstTimeBuyer, IStampDutyYesNoOnlyProperty } from "./types";
import { trackEvent } from "../../../../../helpers/analytics";

export type IProps = {
    propertyValue: string;
    setPropertyValue: (value: string) => void;
    country?: IStampDutyCountry
    setCountry: (value: IStampDutyCountry) => void;
    isFirstTimeBuyer?: IStampDutyYesNoFirstTimeBuyer
    setIsFirstTimeBuyer: (value: IStampDutyYesNoFirstTimeBuyer) => void;
    isOnlyProperty?: IStampDutyYesNoOnlyProperty
    setIsOnlyProperty: (value: IStampDutyYesNoOnlyProperty) => void;
    shouldDisabledIsOnlyProperty: boolean;
};

export const StampDutyForm: React.FC<IProps> = ({
  propertyValue,
  setPropertyValue,
  country,
  setCountry,
  isFirstTimeBuyer,
  setIsFirstTimeBuyer,
  isOnlyProperty,
  setIsOnlyProperty,
  shouldDisabledIsOnlyProperty,
}) => {
  const isOnlyPropertyWithDisable = shouldDisabledIsOnlyProperty ? undefined : isOnlyProperty;
  const radioOnlyProperty = cn(
    "form-group",
    { "radio-disabled": shouldDisabledIsOnlyProperty },
  );
  return (
    <>
      <div className="form-group">
        <Input
          id="propertyValue"
          variant="currency"
          label="What is the property price?"
          inputSize="large"
          name="Property value"
          className="calculator-input"
          value={propertyValue}
          onChange={(e) => {
            trackEvent("Property Value Changed", {
              module: "Stamp Duty Calculator",
              value: e.target.value,
              element_type: "Text field",
            });
            setPropertyValue(e.target.value);
          }}
          required
        />
      </div>
      <div className="form-group">
        <Label
          title="Where are you buying?"
          tooltip={(
            <CalculatorTooltip body={(
              <p className="stamp-duty-calculator-tooltip-p">
                Stamp duty rates can vary depending on where you're buying. Our stamp duty calculator takes that into account.
              </p>
            )}
            />
          )}
          className="calculator-label"
          htmlFor="countryBuying"
        />
        <div className="radio-last-two-inline">
          <Radio
            options={[
              { code: IStampDutyCountry.ENG_NI, description: "England or Northern Ireland" },
              { code: IStampDutyCountry.SCOT, description: "Scotland" },
              { code: IStampDutyCountry.WALES, description: "Wales" },
            ]}
            className="calculator-input"
            inline={false}
            isValid
            value={country}
            onChange={(e) => {
              trackEvent("Country Selected", {
                module: "Stamp Duty Calculator",
                value: e.target.value,
                element_type: "Radio",
              });
              setCountry(IStampDutyCountry[e.target.value as keyof typeof IStampDutyCountry]);
            }}
          />
        </div>
      </div>
      <div className="form-group">
        <Label
          title="Are you a first time buyer?"
          className="calculator-label"
          htmlFor="firstTimeBuyer"
        />
        <Radio
          options={[
            { code: IStampDutyYesNoFirstTimeBuyer.YES_FIRST_TIME_BUYER, description: "Yes" },
            { code: IStampDutyYesNoFirstTimeBuyer.NO_FIRST_TIME_BUYER, description: "No" },
          ]}
          className="calculator-input"
          inline
          isValid
          value={isFirstTimeBuyer}
          onChange={(e) => {
            trackEvent("First Time Buyer Selected", {
              module: "Stamp Duty Calculator",
              value: e.target.value,
              element_type: "Radio",
            });
            setIsFirstTimeBuyer(IStampDutyYesNoFirstTimeBuyer[e.target.value as keyof typeof IStampDutyYesNoFirstTimeBuyer]);
          }}
        />
      </div>
      <div className={radioOnlyProperty}>
        <Label
          title="Will this be your only property?"
          className="calculator-label"
          htmlFor="onlyProperty"
        />
        <Radio
          options={[
            { code: IStampDutyYesNoOnlyProperty.YES_ONLY_PROPERTY, description: "Yes" },
            { code: IStampDutyYesNoOnlyProperty.NO_ONLY_PROPERTY, description: "No" },
          ]}
          className="calculator-input"
          inline
          isValid
          value={isOnlyPropertyWithDisable}
          disabled={shouldDisabledIsOnlyProperty}
          onChange={(e) => {
            trackEvent("Only Property Selected", {
              module: "Stamp Duty Calculator",
              value: e.target.value,
              element_type: "Radio",
            });
            setIsOnlyProperty(IStampDutyYesNoOnlyProperty[e.target.value as keyof typeof IStampDutyYesNoOnlyProperty]);
          }}
        />
      </div>
    </>
  );
};
